import type { ReactNode, SVGProps } from "react";

/**
 * The records pages' glyphs — the prototype's own `ic()` set (register.js:32,
 * admin.js:27), path for path: 24-box, 1.8px stroke, round caps, currentColor.
 * Decorative: the control around them carries the accessible name.
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

function Glyph({ children, ...props }: P & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			focusable={false}
			{...props}
		>
			{children}
		</svg>
	);
}

export const ZapIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M13 3.5 5.5 13.5H12L11 20.5l7.5-10H12Z" />
	</Glyph>
);
export const PrinterIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M7 9V4.5h10V9" />
		<rect x="4" y="9" width="16" height="7.5" rx="1.8" />
		<path d="M7 14h10v5.5H7Z" />
	</Glyph>
);
export const LayersIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m12 4 8.5 4.5L12 13 3.5 8.5Z" />
		<path d="m3.5 12.5 8.5 4.5 8.5-4.5" />
	</Glyph>
);
export const ClockIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<path d="M12 7.5V12l3 2" />
	</Glyph>
);
export const DownloadIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 4.5v11M7.5 11 12 15.5 16.5 11" />
		<path d="M4.5 15v3a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-3" />
	</Glyph>
);
export const GridIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="4" y="4" width="7" height="7" rx="1.5" />
		<rect x="13" y="4" width="7" height="7" rx="1.5" />
		<rect x="4" y="13" width="7" height="7" rx="1.5" />
		<rect x="13" y="13" width="7" height="7" rx="1.5" />
	</Glyph>
);
export const RowsIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M9 7h11M9 12h11M9 17h11M4.5 7h.01M4.5 12h.01M4.5 17h.01" />
	</Glyph>
);
export const LockIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="5.5" y="10.5" width="13" height="9.5" rx="2" />
		<path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
	</Glyph>
);
export const SwapIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M7 7h12M15.5 3.5 19 7l-3.5 3.5M17 17H5M8.5 13.5 5 17l3.5 3.5" />
	</Glyph>
);
export const PlusIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 5v14M5 12h14" />
	</Glyph>
);
/** Her `filter` glyph (three bars narrowing) — the Filters button. */
export const SlidersIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 6.5h16M7 12h10M10 17.5h4" />
	</Glyph>
);
export const ArrowLeftIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M19 12H5M11 6l-6 6 6 6" />
	</Glyph>
);
export const ChevronRightIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m9 6 6 6-6 6" />
	</Glyph>
);
export const ChevronDownIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m6 9 6 6 6-6" />
	</Glyph>
);
export const DotsIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="5.5" cy="12" r="1.2" />
		<circle cx="12" cy="12" r="1.2" />
		<circle cx="18.5" cy="12" r="1.2" />
	</Glyph>
);
export const UndoIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M9 7 4.5 11.5 9 16" />
		<path d="M5 11.5h9.5a5 5 0 0 1 0 10H12" />
	</Glyph>
);
export const AlertTriangleIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 4 2.8 19.5h18.4Z" />
		<path d="M12 10v4.2M12 17v.2" />
	</Glyph>
);
export const TrashIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />
	</Glyph>
);
export const PencilIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M5 19h3.5L18.2 9.3a2 2 0 0 0 0-2.8l-.7-.7a2 2 0 0 0-2.8 0L5 15.5Z" />
		<path d="m13.5 7 3.5 3.5" />
	</Glyph>
);
export const EyeIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
		<circle cx="12" cy="12" r="3" />
	</Glyph>
);
export const CheckIcon = (p: P) => (
	<Glyph {...p}>
		<path d="m5 12.5 4.5 4.5L19 7.5" />
	</Glyph>
);
export const KeyIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="8.5" cy="15.5" r="4" />
		<path d="m11.5 12.5 8-8M16.5 7.5l2.5 2.5M14 10l2 2" />
	</Glyph>
);
/** Her `nophoto` glyph: a person, struck through. */
export const NoPhotoGlyph = (p: P) => (
	<Glyph {...p}>
		<circle cx="12" cy="8.5" r="3.6" />
		<path d="M5 20c.8-3.6 3.6-5.6 7-5.6s6.2 2 7 5.6" />
		<path d="M4 4l16 16" />
	</Glyph>
);
/** Her `card` glyph (an ID card) — MyKad / national ID. */
export const IdCardIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="3" y="5.5" width="18" height="13" rx="2.2" />
		<circle cx="8.5" cy="11" r="2" />
		<path d="M6 15.5c.5-1.3 1.4-2 2.5-2s2 .7 2.5 2M13.5 10h4.5M13.5 13h3" />
	</Glyph>
);
/** Her `passport` glyph. */
export const PassportIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="5.5" y="3.5" width="13" height="17" rx="2" />
		<circle cx="12" cy="10" r="3" />
		<path d="M9 16h6" />
	</Glyph>
);

/* The journey tracker's step glyphs (`journey.tsx`). */
export const UserPlusIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="10" cy="8.5" r="3.6" />
		<path d="M3.5 20c.8-3.6 3.4-5.6 6.5-5.6 1.4 0 2.7.4 3.8 1.2M18 13.5v6M15 16.5h6" />
	</Glyph>
);
export const SendIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M20.5 3.5 3.5 10.5l7 3 3 7Z" />
		<path d="m20.5 3.5-10 10" />
	</Glyph>
);
export const SearchIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="10.5" cy="10.5" r="6" />
		<path d="m15 15 5 5" />
	</Glyph>
);
export const ShieldCheckIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 3.5 5 6v5.5c0 4.3 2.9 7.6 7 9 4.1-1.4 7-4.7 7-9V6Z" />
		<path d="m8.8 12 2.2 2.2 4.2-4.4" />
	</Glyph>
);
export const XIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
	</Glyph>
);
export const GiftIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="4" y="8.5" width="16" height="4" rx="1" />
		<path d="M5.5 12.5v7h13v-7M12 8.5v11M12 8.5C10.5 5 7 4.5 7 6.8c0 1.3 1.8 1.7 5 1.7ZM12 8.5c1.5-3.5 5-4 5-1.7 0 1.3-1.8 1.7-5 1.7Z" />
	</Glyph>
);
export const CopyIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="8.5" y="8.5" width="11" height="11" rx="2" />
		<path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
	</Glyph>
);
